import { StyleSheet } from 'react-native';

import {
    create,
    type ThemedStyleSheet as IThemedStyleSheet,
    template,
    type WebSpecificStyle,
} from './ThemedStyleSheet';

export { ConfigurationTheme, ThemeConfigurationProvider } from './configuration';
export { type Theme, theme } from './theme';
export * from './themed-fonts';
export { PrimaryColorTheme } from './tokens';
export {
    LayoutTheme,
    ResetLayoutTheme,
    useConfigurationTheme,
    useCurrentTheme,
    useInAppThemeConfigured,
    useThemeVariants,
} from './useTheme';
export type { VariantsValue } from './variants';

export type { IThemedStyleSheet, WebSpecificStyle };

/**
 * A ThemedStyleSheet is an theme-aware abstraction similar to CSS StyleSheets
 *
 * Create a new ThemedStyleSheet:
 *
 * ```
 * const themedStyles = ThemedStyleSheet.create({
 *   // a style can be an object, just like react-native's StyleSheet
 *   title: {
 *     fontSize: 19,
 *     fontWeight: 'bold',
 *   },
 *   // but it can also be function which receives the theme as an argument
 *   container: theme => ({
 *     borderRadius: 4,
 *     borderWidth: 0.5,
 *     borderColor: theme.colors.borderMuted,
 *   }),
 *   // and also receives the variants as second argument
 *   activeTitle: (theme, variants) => {
 *     color: variants.color === 'light' ? theme.colors.redDark : theme.colors.redLight,
 *   },
 * });
 * ```
 *
 * Use a ThemedStyleSheet:
 *
 * ```
 * const Component = (props) => {
 *   const [, styles] = useCurrentTheme(themedStyles)
 *
 *   return (
 *     <View style={styles.container}>
 *       <Text style={[styles.title, props.isActive && styles.activeTitle]} />
 *     </View>
 *   )
 * }
 * ```
 *
 * Code quality:
 *
 *  - By moving styles away from the render function, you're making the code
 *  easier to understand.
 *  - Naming the styles is a good way to add meaning to the low level components
 *  in the render function.
 *
 * Performance:
 *
 *  - Making a stylesheet from a style object makes it possible to refer to it
 * by ID instead of creating a new style object every time.
 *  - It also allows to send the style only once through the bridge. All
 * subsequent uses are going to refer an id (not implemented yet).
 */
export const ThemedStyleSheet = {
    /**
     * Creates a ThemedStyleSheet style reference from the given object.
     */
    create,

    // Maintain the same API as StyleSheet

    /**
     * Flattens an array of style objects, into one aggregated style object.
     * Alternatively, this method can be used to lookup IDs, returned by
     * ThemedStyleSheet.create.
     *
     * > **NOTE**: Exercise caution as abusing this can tax you in terms of
     * > optimizations.
     * >
     * > IDs enable optimizations through the bridge and memory in general. Refering
     * > to style objects directly will deprive you of these optimizations.
     *
     * Example:
     * ```
     * const styles = ThemedStyleSheet.create(theme => {
     *   listItem: {
     *     flex: 1,
     *     fontSize: 16,
     *     color: 'white'
     *   },
     *   selectedListItem: {
     *     color: 'green'
     *   }
     * });
     *
     * StyleSheet.flatten([styles.listItem, styles.selectedListItem])
     * // returns { flex: 1, fontSize: 16, color: 'green' }
     * ```
     * Alternative use:
     * ```
     * StyleSheet.flatten(styles.listItem);
     * // return { flex: 1, fontSize: 16, color: 'white' }
     * // Simply styles.listItem would return its ID (number)
     * ```
     * This method internally uses `StyleSheetRegistry.getStyleByID(style)`
     * to resolve style objects represented by IDs. Thus, an array of style
     * objects (instances of StyleSheet.create), are individually resolved to,
     * their respective objects, merged as one and then returned. This also explains
     * the alternative use.
     */
    flatten: StyleSheet.flatten,
    /**
     * This is defined as the width of a thin line on the platform. It can be
     * used as the thickness of a border or division between two elements.
     * Example:
     * ```
     *   {
     *     borderBottomColor: '#bbb',
     *     borderBottomWidth: ThemedStyleSheet.hairlineWidth
     *   }
     * ```
     *
     * This constant will always be a round number of pixels (so a line defined
     * by it look crisp) and will try to match the standard width of a thin line
     * on the underlying platform. However, you should not rely on it being a
     * constant size, because on different platforms and screen densities its
     * value may be calculated differently.
     */
    hairlineWidth: StyleSheet.hairlineWidth,
    /**
     * Sometimes you may want `absoluteFill` but with a couple tweaks - `absoluteFillObject` can be
     * used to create a customized entry in a `ThemedStyleSheet`, e.g.:
     *
     *   const styles = ThemedStyleSheet.create(theme => {
     *     wrapper: {
     *       ...ThemedStyleSheet.absoluteFillObject,
     *       top: 10,
     *       backgroundColor: 'transparent',
     *     },
     *   });
     */
    absoluteFillObject: StyleSheet.absoluteFillObject,
    /**
     * A very common pattern is to create overlays with position absolute and zero positioning,
     * so `absoluteFill` can be used for convenience and to reduce duplication of these repeated
     * styles.
     */
    absoluteFill: StyleSheet.absoluteFill,
    /**
     * Utility to use tokens in a template string.
     * Some tokens are dynamic, and so doesn't play well when used in a template string
     * (they are a function so the function is transformed into a string)
     * Prefixing the template string with this function makes sure that the theme resolution
     * knows what it's dealing with.
     *
     * @example
     * ThemedStyleSheet.create({
     *   style: theme => ({
     *     backgroundImage: ThemedStyleSheet.template`linear-gradient(145deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.standout} 100%)`
     *   })
     * })
     */
    template,
};
